import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import LegalFooter from '../components/LegalFooter';

const AUTHORITY_SITE = 'https://aiaccountabilitycouncil.com';

export default function Privacy() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="border-b border-zinc-200">
        <div className="container mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <Link to="/" className="header-logo text-xl">AASC Registry</Link>
              <a 
                href={AUTHORITY_SITE}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-zinc-600 hover:text-zinc-900 flex items-center gap-1 border-l border-zinc-200 pl-6"
              >
                AI Accountability Standards Council <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <nav className="flex items-center gap-6">
              <Link to="/registry" className="nav-link">Registry</Link>
              <Link to="/apply" className="nav-link">Apply</Link>
            </nav>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-8 py-12 max-w-4xl flex-1">
        <h1 className="text-4xl font-bold mb-8" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Privacy Policy
        </h1>

        {/* Authority Site Reference */}
        <div className="bg-blue-50 border border-blue-200 p-4 mb-8">
          <p className="text-sm text-blue-800">
            This Privacy Policy mirrors the official policy published by the AI Accountability Standards Council.{' '}
            <a 
              href={`${AUTHORITY_SITE}/privacy-policy`}
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-medium inline-flex items-center gap-1"
            >
              View authoritative version <ExternalLink className="w-3 h-3" />
            </a>
          </p>
        </div>

        <div className="prose prose-zinc max-w-none">
          <p className="text-lg text-zinc-600">Effective Date: June 2025</p>

          <h2>1. Overview</h2>
          <p>
            This Privacy Policy explains how the AI Accountability Standards Council ("AASC") collects, uses, 
            and protects information when you access AASC websites, declaration services, or resources.
          </p>
          <p>
            By using any AASC resource, you consent to the practices described in this policy.
          </p>

          <h2>2. Information Collected</h2>
          <p>AASC may collect the following types of information:</p>
          <ul>
            <li><strong>Contact Information:</strong> Names, email addresses, organization names, and roles provided voluntarily through forms or applications.</li>
            <li><strong>Declaration Data:</strong> Documentation, evidence, and attestations submitted as part of declaration applications.</li>
            <li><strong>Technical Data:</strong> IP addresses, browser type, device information, and access timestamps collected automatically via server logs.</li>
          </ul>

          <h2>3. Use of Information</h2>
          <p>AASC uses collected information for the following purposes:</p>
          <ul>
            <li>Processing and reviewing declaration applications.</li>
            <li>Maintaining the public declaration registry.</li>
            <li>Communicating about declaration status, renewals, and standards updates.</li>
            <li>Improving AASC services and website functionality.</li>
            <li>Complying with legal obligations.</li>
          </ul>

          <h2>4. Public Registry</h2>
          <p>
            The AASC declaration registry is public. Organizations that hold active declarations or 
            alignment declarations will have certain information displayed publicly, including:
          </p>
          <ul>
            <li>Organization name</li>
            <li>Website (if provided)</li>
            <li>Declaration status</li>
            <li>Standards declared against</li>
            <li>Issue and expiry dates</li>
          </ul>
          <p>
            Internal documentation and assessment results are not published.
          </p>

          <h2>5. Data Sharing</h2>
          <p>AASC does not sell personal data. AASC may share information in the following cases:</p>
          <ul>
            <li>With service providers who assist in operating AASC services, subject to confidentiality obligations.</li>
            <li>As required by law, legal process, or government request.</li>
            <li>To protect the rights, safety, or property of AASC or third parties.</li>
          </ul>

          <h2>6. Data Security</h2>
          <p>
            AASC implements reasonable technical and organizational measures to protect data from 
            unauthorized access, loss, or misuse. However, no system is completely secure, and 
            AASC cannot guarantee absolute security.
          </p>

          <h2>7. Data Retention</h2>
          <p>
            AASC retains declaration records for as long as necessary to fulfill the purposes 
            described in this policy or as required by law. Organizations may request deletion 
            of application data subject to legal and operational constraints.
          </p>

          <h2>8. Your Rights</h2>
          <p>Depending on your jurisdiction, you may have the right to:</p>
          <ul>
            <li>Access, correct, or delete your personal data.</li>
            <li>Object to or restrict certain data processing.</li>
            <li>Withdraw consent where processing is based on consent.</li>
          </ul>
          <p>
            To exercise these rights, contact{' '}
            <a href="mailto:privacy@aiaccountabilitycouncil.com" className="text-blue-600 hover:underline">
              privacy@aiaccountabilitycouncil.com
            </a>.
          </p>

          <h2>9. Cookies</h2>
          <p>
            AASC websites may use cookies and similar technologies for functionality and analytics. 
            You may control cookies through your browser settings.
          </p>

          <h2>10. Policy Changes</h2>
          <p>
            AASC may update this Privacy Policy periodically. Changes will be posted on this page 
            with an updated effective date.
          </p>

          <h2>11. Contact</h2>
          <p>
            For privacy-related inquiries, please contact the Secretariat at{' '}
            <a href="mailto:privacy@aiaccountabilitycouncil.com" className="text-blue-600 hover:underline">
              privacy@aiaccountabilitycouncil.com
            </a>.
          </p>
        </div>
      </main>

      <LegalFooter />
    </div>
  );
}
